export type MerchantOperationMethod = "DELETE" | "GET" | "PATCH" | "POST" | "PUT";

export interface MerchantOperationDescriptor {
  readonly id: string;
  readonly method: MerchantOperationMethod;
  readonly path: string;
}

export type MerchantOperationPathParams = Record<string, string | number>;
export type MerchantOperationQuery = Record<string, unknown>;

export const MERCHANT_OPERATIONS = {
  listMerchantFeedback: operation("listMerchantFeedback", "GET", "/v1/api/feedback"),
  createMerchantFeedback: operation("createMerchantFeedback", "POST", "/v1/api/feedback"),
  listMerchantFeedbackReplies: operation(
    "listMerchantFeedbackReplies",
    "GET",
    "/v1/api/feedback/{feedbackId}/replies",
  ),
  createMerchantFeedbackReply: operation(
    "createMerchantFeedbackReply",
    "POST",
    "/v1/api/feedback/{feedbackId}/replies",
  ),
  getMerchantBilling: operation("getMerchantBilling", "GET", "/v1/api/billing"),
  refreshMerchantBilling: operation("refreshMerchantBilling", "POST", "/v1/api/billing/refresh"),
  approveObservedCompetitorDomain: operation(
    "approveObservedCompetitorDomain",
    "POST",
    "/v1/api/competitors/observed",
  ),
  assignMerchantRequest: operation(
    "assignMerchantRequest",
    "POST",
    "/v1/api/requests/{requestId}/assignment",
  ),
  bootstrapShopifySession: operation(
    "bootstrapShopifySession",
    "POST",
    "/v1/shopify/session/bootstrap",
  ),
  createCompetitor: operation("createCompetitor", "POST", "/v1/api/competitors"),
  deleteCompetitor: operation("deleteCompetitor", "DELETE", "/v1/api/competitors/{competitorId}"),
  createMerchantRequestCustomerNote: operation(
    "createMerchantRequestCustomerNote",
    "POST",
    "/v1/api/requests/{requestId}/customer-notes",
  ),
  createMerchantRequestInternalNote: operation(
    "createMerchantRequestInternalNote",
    "POST",
    "/v1/api/requests/{requestId}/internal-notes",
  ),
  createMerchantRequestMessage: operation(
    "createMerchantRequestMessage",
    "POST",
    "/v1/api/requests/{requestId}/messages",
  ),
  createMerchantRequestAttachmentUploadUrl: operation(
    "createMerchantRequestAttachmentUploadUrl",
    "POST",
    "/v1/api/requests/{requestId}/attachments/upload-url",
  ),
  decideMerchantRequest: operation(
    "decideMerchantRequest",
    "POST",
    "/v1/api/requests/{requestId}/decision",
  ),
  escalateOverdueRequests: operation(
    "escalateOverdueRequests",
    "POST",
    "/v1/api/requests/escalate-overdue",
  ),
  getAppStoreProfile: operation("getAppStoreProfile", "GET", "/v1/api/store"),
  getCatalogProduct: operation("getCatalogProduct", "GET", "/v1/api/products/{productId}"),
  getCompetitor: operation("getCompetitor", "GET", "/v1/api/competitors/{competitorId}"),
  getMerchantAuditEntry: operation("getMerchantAuditEntry", "GET", "/v1/api/audit/{auditId}"),
  getMerchantDashboard: operation("getMerchantDashboard", "GET", "/v1/api/dashboard"),
  getMerchantJobStatus: operation("getMerchantJobStatus", "GET", "/v1/api/jobs/{jobId}"),
  getMerchantNotificationPreferences: operation(
    "getMerchantNotificationPreferences",
    "GET",
    "/v1/api/notification-preferences",
  ),
  getMerchantOnboarding: operation("getMerchantOnboarding", "GET", "/v1/api/onboarding"),
  getMerchantRequest: operation("getMerchantRequest", "GET", "/v1/api/requests/{requestId}"),
  getMerchantRequestCustomerLinkCandidates: operation(
    "getMerchantRequestCustomerLinkCandidates",
    "GET",
    "/v1/api/requests/{requestId}/customer-link-candidates",
  ),
  linkMerchantRequestCustomer: operation(
    "linkMerchantRequestCustomer",
    "POST",
    "/v1/api/requests/{requestId}/customer-link",
  ),
  resendMerchantRequestCustomerEmail: operation(
    "resendMerchantRequestCustomerEmail",
    "POST",
    "/v1/api/requests/{requestId}/resend-customer-email",
  ),
  retryMerchantRequestCoupon: operation(
    "retryMerchantRequestCoupon",
    "POST",
    "/v1/api/requests/{requestId}/coupon-retry",
  ),
  getMerchantRequestAttachmentDownloadUrl: operation(
    "getMerchantRequestAttachmentDownloadUrl",
    "GET",
    "/v1/api/requests/{requestId}/attachments/{attachmentId}/download-url",
  ),
  getPriceMatchPolicyPage: operation(
    "getPriceMatchPolicyPage",
    "GET",
    "/v1/api/policies/price-match/page",
  ),
  listPriceMatchPolicyHistory: operation(
    "listPriceMatchPolicyHistory",
    "GET",
    "/v1/api/policies/price-match/history",
  ),
  deleteProductPriceMatchPolicy: operation(
    "deleteProductPriceMatchPolicy",
    "DELETE",
    "/v1/api/products/{productId}/policy",
  ),
  getProductPriceMatchPolicy: operation(
    "getProductPriceMatchPolicy",
    "GET",
    "/v1/api/products/{productId}/policy",
  ),
  getProductMapping: operation("getProductMapping", "GET", "/v1/api/mappings/{mappingId}"),
  getStorefrontWidgetConfig: operation("getStorefrontWidgetConfig", "GET", "/v1/api/widget"),
  listCatalogProducts: operation("listCatalogProducts", "GET", "/v1/api/products"),
  listCompetitors: operation("listCompetitors", "GET", "/v1/api/competitors"),
  listMerchantAuditHistory: operation("listMerchantAuditHistory", "GET", "/v1/api/audit"),
  listMerchantNotifications: operation("listMerchantNotifications", "GET", "/v1/api/notifications"),
  listStoreAttentionNotifications: operation(
    "listStoreAttentionNotifications",
    "GET",
    "/v1/api/notifications/attention",
  ),
  listMerchantRequests: operation("listMerchantRequests", "GET", "/v1/api/requests"),
  listProductMappings: operation("listProductMappings", "GET", "/v1/api/mappings"),
  previewPriceMatchPolicy: operation(
    "previewPriceMatchPolicy",
    "POST",
    "/v1/api/policies/price-match/preview",
  ),
  requestAppStoreDataDeletion: operation(
    "requestAppStoreDataDeletion",
    "POST",
    "/v1/api/store/data-deletion",
  ),
  requestAppStoreUninstall: operation(
    "requestAppStoreUninstall",
    "POST",
    "/v1/api/store/uninstall",
  ),
  refreshAppStoreIntegrationHealth: operation(
    "refreshAppStoreIntegrationHealth",
    "POST",
    "/v1/api/store/integration-health/refresh",
  ),
  retryMerchantRequestEvidence: operation(
    "retryMerchantRequestEvidence",
    "POST",
    "/v1/api/requests/{requestId}/retry",
  ),
  selectMerchantRequestEvidence: operation(
    "selectMerchantRequestEvidence",
    "POST",
    "/v1/api/requests/{requestId}/evidence-selection",
  ),
  completeMerchantRequestAttachmentUpload: operation(
    "completeMerchantRequestAttachmentUpload",
    "POST",
    "/v1/api/requests/{requestId}/attachments/{attachmentId}/complete",
  ),
  retryNotificationDelivery: operation(
    "retryNotificationDelivery",
    "POST",
    "/v1/api/notifications/{notificationId}/retry",
  ),
  runMerchantOnboardingAction: operation(
    "runMerchantOnboardingAction",
    "POST",
    "/v1/api/onboarding/actions/{action}",
  ),
  startMerchantProductSync: operation("startMerchantProductSync", "POST", "/v1/api/products/sync"),
  testCompetitorRetrieval: operation(
    "testCompetitorRetrieval",
    "POST",
    "/v1/api/competitors/{competitorId}/test",
  ),
  updateAppStoreSettings: operation("updateAppStoreSettings", "PATCH", "/v1/api/store"),
  updateCatalogProductEligibility: operation(
    "updateCatalogProductEligibility",
    "PATCH",
    "/v1/api/products/{productId}/eligibility",
  ),
  updateCatalogProductEligibilityBulk: operation(
    "updateCatalogProductEligibilityBulk",
    "POST",
    "/v1/api/products/eligibility/bulk",
  ),
  previewCatalogProductEligibilityReset: operation(
    "previewCatalogProductEligibilityReset",
    "POST",
    "/v1/api/products/eligibility/reset/preview",
  ),
  previewProductBulkMonitoring: operation(
    "previewProductBulkMonitoring",
    "POST",
    "/v1/api/products/monitoring/bulk/preview",
  ),
  previewProductMappingChecks: operation(
    "previewProductMappingChecks",
    "POST",
    "/v1/api/products/mapping-checks/bulk/preview",
  ),
  queueProductMappingChecks: operation(
    "queueProductMappingChecks",
    "POST",
    "/v1/api/products/mapping-checks/bulk",
  ),
  queueProductMappingCheck: operation(
    "queueProductMappingCheck",
    "POST",
    "/v1/api/mappings/{mappingId}/monitoring/check",
  ),
  listProductMappingMonitoringObservations: operation(
    "listProductMappingMonitoringObservations",
    "GET",
    "/v1/api/mappings/{mappingId}/monitoring/observations",
  ),
  resetCatalogProductEligibilityBulk: operation(
    "resetCatalogProductEligibilityBulk",
    "POST",
    "/v1/api/products/eligibility/reset",
  ),
  repairCatalogProducts: operation("repairCatalogProducts", "POST", "/v1/api/products/repair"),
  updateProductBulkMonitoring: operation(
    "updateProductBulkMonitoring",
    "POST",
    "/v1/api/products/monitoring/bulk",
  ),
  updateCompetitor: operation("updateCompetitor", "PATCH", "/v1/api/competitors/{competitorId}"),
  updateMerchantRequestCustomerNote: operation(
    "updateMerchantRequestCustomerNote",
    "PATCH",
    "/v1/api/requests/{requestId}/customer-notes/{noteId}",
  ),
  updateMerchantRequestInternalNote: operation(
    "updateMerchantRequestInternalNote",
    "PATCH",
    "/v1/api/requests/{requestId}/internal-notes/{noteId}",
  ),
  removeMerchantRequestAttachment: operation(
    "removeMerchantRequestAttachment",
    "DELETE",
    "/v1/api/requests/{requestId}/attachments/{attachmentId}",
  ),
  updateMerchantNotificationPreferences: operation(
    "updateMerchantNotificationPreferences",
    "PUT",
    "/v1/api/notification-preferences",
  ),
  updateMerchantNotificationReadState: operation(
    "updateMerchantNotificationReadState",
    "PATCH",
    "/v1/api/notifications/{notificationId}",
  ),
  markStoreAttentionNotificationRead: operation(
    "markStoreAttentionNotificationRead",
    "PATCH",
    "/v1/api/notifications/attention/{notificationId}",
  ),
  updatePriceMatchPolicy: operation(
    "updatePriceMatchPolicy",
    "PUT",
    "/v1/api/policies/price-match",
  ),
  updateProductPriceMatchPolicy: operation(
    "updateProductPriceMatchPolicy",
    "PUT",
    "/v1/api/products/{productId}/policy",
  ),
  updateStorefrontWidgetConfig: operation("updateStorefrontWidgetConfig", "PUT", "/v1/api/widget"),
  validatePriceMatchPolicy: operation(
    "validatePriceMatchPolicy",
    "POST",
    "/v1/api/policies/price-match/validate",
  ),
} as const satisfies Record<string, MerchantOperationDescriptor>;

export type MerchantOperationId = keyof typeof MERCHANT_OPERATIONS;

export function merchantOperationPath(
  operation: MerchantOperationDescriptor,
  pathParams: MerchantOperationPathParams = {},
  query: MerchantOperationQuery = {},
): string {
  const path = interpolatePathParams(operation.path, pathParams);
  const queryString = toQueryString(query);
  return queryString.length === 0 ? path : `${path}?${queryString}`;
}

function operation(
  id: string,
  method: MerchantOperationMethod,
  path: string,
): MerchantOperationDescriptor {
  return { id, method, path };
}

function interpolatePathParams(path: string, pathParams: MerchantOperationPathParams): string {
  return path.replace(/\{([^}]+)\}/g, (_, key: string) => {
    const value = pathParams[key];
    if (value === undefined) {
      throw new Error(`Missing OpenAPI path parameter: ${key}`);
    }
    return encodeURIComponent(String(value));
  });
}

function toQueryString(query: MerchantOperationQuery): string {
  return new URLSearchParams(
    Object.entries(query)
      .filter(([, value]) => value !== undefined && value !== null && value !== "")
      .map(([key, value]) => [key, String(value)]),
  ).toString();
}
